import { useFindHostsQuery } from '@services/queries/use-find-hosts.query.ts';
import { useFindModelsQuery } from '@services/queries/use-find-models.query.ts';
import { useCreateClientMutation } from '@services/mutations/use-create-client.mutation.ts';
import type { SelectItemProps, PaperProps } from '@components/atoms';
import { Paper, Stack, Typography, Loader } from '@components/atoms';
import { useCallback } from 'react';
import { Form, FormButton, FormSelect, FormTextField } from '@components/forms';
import { Locales } from '@locales/en.ts';
import { CustomHostTextField } from '@views/Home/components/CustomHostTextField.tsx';
import * as yup from 'yup';
import { CustomModelTextFields } from '@views/Home/components/CustomModelTextFields.tsx';

export const CUSTOM_HOST_VALUE = 'Custom host';
export const CUSTOM_MODEL_VALUE = 'Custom model';

export const formSchema = yup
  .object({
    cpid: yup.string().required(Locales.validation.required),
    basicAuth: yup.string().optional(),
    host: yup.string().required(Locales.validation.required),
    model: yup.string().optional(),
    customHost: yup.string().when('host', {
      is: (host: string) => host === CUSTOM_HOST_VALUE,
      then: schema => schema.required(Locales.validation.required),
      otherwise: schema => schema.optional(),
    }),
    customVendor: yup.string().when('model', {
      is: (model: string) => model === CUSTOM_MODEL_VALUE,
      then: schema => schema.required(Locales.validation.required),
      otherwise: schema => schema.optional(),
    }),
    customModel: yup.string().when('model', {
      is: (model: string) => model === CUSTOM_MODEL_VALUE,
      then: schema => schema.required(Locales.validation.required),
      otherwise: schema => schema.optional(),
    }),
  })
  .required();

export type FormInput = yup.InferType<typeof formSchema>;

export const CreateVirtualEVSEForm = () => {
  const { data: hosts, isLoading: isLoadingHosts } = useFindHostsQuery();
  const { data: models, isLoading: isLoadingModels } = useFindModelsQuery();
  const { mutateAsync: createClient, isLoading: isCreatingClient } = useCreateClientMutation();

  const paperStyles: PaperProps['sx'] = { padding: 4, minHeight: 300, minWidth: 400 };

  const hostsItems: SelectItemProps[] = [...(hosts || []), [Locales.labels.custom, CUSTOM_HOST_VALUE]].map(
    ([key, value]) => ({
      listItemProps: {
        primary: key,
        secondary: value,
      },
      key,
      value,
    }),
  );

  const modelsItems: SelectItemProps[] = [...(models || []), [Locales.labels.custom, CUSTOM_MODEL_VALUE]].map(
    ([key, value]) => ({
      listItemProps: {
        primary: key,
        secondary: value,
      },
      key,
      value,
    }),
  );

  const handleSubmit = useCallback(
    async (data: FormInput) => {
      const isCustomHost = data.host === CUSTOM_HOST_VALUE;
      const isCustomModel = data.model === CUSTOM_MODEL_VALUE;
      const vendor = models?.find(([, model]) => model === data.model)?.[0];

      await createClient({
        cpid: data.cpid,
        host: isCustomHost && data.customHost ? data.customHost : data.host,
        basicAuth: data.basicAuth,
        model: isCustomModel && data.customModel ? data.customModel : data.model,
        vendor: isCustomModel && data.customVendor ? data.customVendor : vendor,
      });
    },
    [createClient, models],
  );

  if (isLoadingHosts) {
    return (
      <Paper sx={paperStyles}>
        <Loader position='relative' />
      </Paper>
    );
  }

  return (
    <Paper sx={paperStyles}>
      <Form<FormInput>
        onSubmit={handleSubmit}
        schema={formSchema}
        defaultValues={{
          cpid: 'TESTVIRTUAL01',
          host: (hostsItems || []).find(({ key }) => key.toLowerCase() === 'localhost')?.value?.toString(),
        }}
      >
        <Typography variant='h5' component='p' marginBottom={3}>
          {Locales.homePage.createVirtualEVSE}
        </Typography>
        <Stack direction='column' spacing={2} minWidth={300}>
          <FormTextField name={'cpid' as keyof FormInput} label={Locales.labels.cpid} required />
          <FormTextField name={'basicAuth' as keyof FormInput} label={Locales.labels.basicAuth} type='password' />
          <FormSelect
            name={'model' as keyof FormInput}
            label={Locales.labels.model}
            placeholder={Locales.labels.model}
            items={modelsItems}
            disabled={isLoadingHosts}
          />
          <CustomModelTextFields />
          <FormSelect
            name={'host' as keyof FormInput}
            label={Locales.labels.host}
            placeholder={Locales.labels.host}
            items={hostsItems}
            disabled={isLoadingHosts}
          />
          <CustomHostTextField />
          <FormButton type='submit' disabled={isLoadingHosts || isLoadingModels || isCreatingClient}>
            {Locales.actions.start}
          </FormButton>
        </Stack>
      </Form>
    </Paper>
  );
};
